// The deals list. Search, filter, sort and paging all happen at the API.
//
// This is the screen where "client-side over one page" would be a lie: with 25
// rows on screen out of a growing table, sorting the current page by renewal
// date shows the earliest renewal *of this page*, which is not the question
// anyone asked. So every control here writes into `DealsQueryState`, that state
// becomes the query string (§06), and the table renders exactly the rows that
// came back, in exactly the order they came back in.
//
// That is also why this is a plain `<table>` and not a grid component: a grid
// that owns sorting in the client would spend this screen's whole life fighting
// the API for control of the same state. A header here is a button that changes
// the QUERY — the rows never move on their own.

import { Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { dealHasCoordinates, dealHasLocation, dealInheritsTerms, DealState } from "@crm/shared";
import type { Deal, DealCreate, DealListItem, DealUpdate, SortDirection } from "@crm/shared";
import styles from "./DealsList.module.css";
import { Badge, toneFor } from "../../design/Badge.js";
import { Button } from "../../design/Button.js";
import { Card } from "../../design/Card.js";
import { Notice, ProblemMessage } from "../../design/Feedback.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { EmptyRow, Table, Td, Th, tableStyles } from "../../design/Table.js";
import { DeleteDialog } from "../../forms/DeleteDialog.js";
import { PartnerSwatch } from "../../components/PartnerSwatch.js";
import { useDealMutations, type ResourceMutations } from "../../api/mutations.js";
import { isApiProblem } from "../../api/problem.js";
import { useHasRole } from "../../auth/useAuth.js";
import { formatCalendarDate, formatDateRange } from "../../format/calendarDate.js";
import { formatCount, formatPercent } from "../../format/money.js";
import { countryName } from "../../format/countries.js";
import { dealStateColor, dealStateLabel } from "../../format/labels.js";
import type { DealSortableColumn } from "./dealColumns.js";
import { DealsToolbar, dealsFiltersActive } from "./DealsToolbar.js";
import type { DealsViewMode } from "./dealsSearch.js";
import { splitCoverageByDeal, type SplitCoverage } from "./dealClauses.js";
import {
  useDeals,
  usePartnerColors,
  useTermLinesForDeals,
  type DealsQueryState,
} from "../../api/queries.js";

type DealMutations = ResourceMutations<Deal, DealCreate, DealUpdate>;

export interface DealsListProps {
  state: DealsQueryState;
  onStateChange: (next: DealsQueryState) => void;
  /** Which view is showing, and how to switch — the toolbar's toggle. */
  view: DealsViewMode;
  onViewChange: (next: DealsViewMode) => void;
  /** Absent for a reader — the create action is the page's to own and to gate. */
  onCreate?: () => void;
}

export function DealsList({
  state,
  onStateChange,
  view,
  onViewChange,
  onCreate,
}: DealsListProps): ReactNode {
  const query = useDeals(state);
  const mutations = useDealMutations();
  const canEdit = useHasRole("crm_editor");
  const partnerColors = usePartnerColors();
  const [deleting, setDeleting] = useState<DealListItem | null>(null);

  // Whether a deal with no agreement NEEDS one is a question only its clauses
  // answer, and the row is what states it. One read for the whole page, and no
  // read at all for a page whose deals all resolve an agreement.
  const unagreedIds = (query.data?.items ?? [])
    .filter((item) => item.effective_agreement_id === null)
    .map((item) => item.id);
  const clauses = useTermLinesForDeals(unagreedIds);
  const coverage = splitCoverageByDeal(unagreedIds, clauses.data?.items);

  const filtersActive = dealsFiltersActive(state);
  const columnCount = canEdit ? 10 : 9;
  const total = query.data?.total;

  return (
    <div className={styles.root}>
      <DealsToolbar
        state={state}
        onStateChange={onStateChange}
        view={view}
        onViewChange={onViewChange}
        total={total}
        {...(onCreate === undefined ? {} : { onCreate })}
      />

      <QueryBoundary query={query} label="the deals list">
        {(page) => (
          <>
            <Card>
              <Table label="Deals">
                <thead>
                  <tr>
                    <SortableHeader
                      column="customer_name"
                      label="Customer"
                      state={state}
                      onStateChange={onStateChange}
                    />
                    {/* Not in DEAL_SORT_FIELDS, so it stays plain text: a header
                        offering a sort the API answers with
                        `unsupported_sort_field` is a control that reliably 400s. */}
                    <Th>Partner</Th>
                    {/* The agreement whose terms actually apply, which for an
                        inheriting deal is on another partner's paper — see the
                        cell. Not sortable for the same reason as Partner. */}
                    <Th>Agreement</Th>
                    <SortableHeader
                      column="state"
                      label="State"
                      state={state}
                      onStateChange={onStateChange}
                    />
                    <SortableHeader
                      column="win_prob"
                      label="Win %"
                      numeric
                      state={state}
                      onStateChange={onStateChange}
                    />
                    <Th>Cur</Th>
                    <SortableHeader
                      column="start_date_earliest"
                      label="Start"
                      state={state}
                      onStateChange={onStateChange}
                    />
                    <SortableHeader
                      column="renewal_date"
                      label="Renewal"
                      state={state}
                      onStateChange={onStateChange}
                    />
                    <SortableHeader
                      column="customer_country"
                      label="Location"
                      state={state}
                      onStateChange={onStateChange}
                    />
                    {canEdit && (
                      <Th>
                        <span className="srOnly">Row actions</span>
                      </Th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {page.items.length === 0 ? (
                    <EmptyRow
                      colSpan={columnCount}
                      title="No deals match these filters"
                      description={
                        filtersActive
                          ? "Clear the search or the state filter to see the rest of the pipeline."
                          : "Seed the database (npm run seed) to get a realistic set of deals to click through."
                      }
                    />
                  ) : (
                    page.items.map((deal) => (
                      <DealRow
                        key={deal.id}
                        deal={deal}
                        partnerColors={partnerColors}
                        coverage={coverage.get(deal.id)}
                        canEdit={canEdit}
                        mutations={mutations}
                        columnCount={columnCount}
                        onDelete={() => setDeleting(deal)}
                      />
                    ))
                  )}
                </tbody>
              </Table>

              {page.items.length > 0 && (
                <Pager
                  total={page.total}
                  limit={page.limit}
                  offset={page.offset}
                  onPage={(nextPage) => onStateChange({ ...state, page: nextPage })}
                />
              )}
            </Card>

            {canEdit && (
              <p className={styles.hint}>
                Fields with a dashed underline can be edited in place — the change is saved against
                the version on screen.
              </p>
            )}
          </>
        )}
      </QueryBoundary>

      {deleting !== null && (
        <DeleteDialog<Deal>
          open
          onClose={() => setDeleting(null)}
          title="Delete deal"
          description={`${deleting.customer_name} will be removed, and its term lines with it. Every period they bill stops billing. An invoice that already went out keeps its own frozen figures, because an issued document never moves.`}
          record={deleting}
          remove={(id, version) => mutations.remove.mutateAsync({ id, version })}
          fetchCurrent={mutations.fetchCurrent}
          onDeleted={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

interface SortableHeaderProps {
  column: DealSortableColumn;
  label: string;
  numeric?: boolean;
  state: DealsQueryState;
  onStateChange: (next: DealsQueryState) => void;
}

function SortableHeader({
  column,
  label,
  numeric,
  state,
  onStateChange,
}: SortableHeaderProps): ReactNode {
  const active = state.sort.field === column;
  const direction: SortDirection = active && state.sort.direction === "asc" ? "desc" : "asc";
  // Announced by assistive tech, and only on the column the API actually
  // ordered by — every other header is offering a sort, not describing one.
  const sortedAs = active
    ? state.sort.direction === "asc"
      ? ("ascending" as const)
      : ("descending" as const)
    : undefined;

  return (
    <Th
      {...(numeric === undefined ? {} : { numeric })}
      {...(sortedAs === undefined ? {} : { "aria-sort": sortedAs })}
    >
      <button
        type="button"
        className={tableStyles.sortable}
        // A new sort re-orders the whole result set, so page 1 is the only page
        // that still means anything.
        onClick={() => onStateChange({ ...state, sort: { field: column, direction }, page: 1 })}
      >
        {label}
        {active && (
          <span className={tableStyles.sortArrow} aria-hidden="true">
            {state.sort.direction === "asc" ? "▲" : "▼"}
          </span>
        )}
      </button>
    </Th>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/**
 * One in-place edit, carried whole. Holding the body and its wording together
 * is what lets the 409 recovery re-send the SAME intent against a newer version
 * without re-deriving what the user asked for.
 */
interface PendingEdit {
  field: "state" | "win_prob";
  body: DealUpdate;
  /** The intent, as a sentence fragment: "the state to Won". */
  described: string;
}

interface DealRowProps {
  deal: DealListItem;
  partnerColors: ReadonlyMap<string, string>;
  /** What an absent agreement costs this deal. Undefined until its clauses land. */
  coverage: SplitCoverage | undefined;
  canEdit: boolean;
  mutations: DealMutations;
  columnCount: number;
  onDelete: () => void;
}

function DealRow({
  deal,
  partnerColors,
  coverage,
  canEdit,
  mutations,
  columnCount,
  onDelete,
}: DealRowProps): ReactNode {
  const navigate = useNavigate();
  const [editing, setEditing] = useState<PendingEdit["field"] | null>(null);
  const [saving, setSaving] = useState(false);
  const [stale, setStale] = useState<PendingEdit | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  const apply = useCallback(
    async (edit: PendingEdit, version: number): Promise<void> => {
      setFailure(null);
      setSaving(true);
      try {
        await mutations.update.mutateAsync({ id: deal.id, body: edit.body, version });
        setStale(null);
        setEditing(null);
      } catch (error) {
        // A 409 is not a failure to report and forget: the edit was refused
        // because this row is older than the stored one, and re-applying it is
        // a decision only the person who made it can take.
        if (isApiProblem(error) && error.code === "crm-conflict") setStale(edit);
        else setFailure(error);
      } finally {
        setSaving(false);
      }
    },
    [deal.id, mutations.update],
  );

  const applyToCurrent = useCallback((): void => {
    if (stale === null) return;
    void (async () => {
      try {
        const current = await mutations.fetchCurrent(deal.id);
        await apply(stale, current.version);
      } catch (error) {
        setFailure(error);
      }
    })();
  }, [apply, deal.id, mutations, stale]);

  const openDeal = (event: MouseEvent<HTMLTableRowElement>): void => {
    // Anything the row contains that is itself a control keeps its own click.
    const target = event.target;
    if (target instanceof Element && target.closest("a,button,select,input,label") !== null) return;
    void navigate({ to: "/deals/$dealId", params: { dealId: deal.id } });
  };

  return (
    <>
      <tr className={tableStyles.rowLink} onClick={openDeal}>
        <Td strong>
          <Link to="/deals/$dealId" params={{ dealId: deal.id }} className={styles.customerLink}>
            {deal.customer_name}
          </Link>
        </Td>
        <Td>
          <Link to="/partners/$partnerId" params={{ partnerId: deal.partner_id }}>
            <PartnerSwatch color={partnerColors.get(deal.partner_id) ?? null} />
            {deal.partner_name}
          </Link>
        </Td>
        <Td>
          <AgreementCell deal={deal} coverage={coverage} />
        </Td>
        <Td>
          <StateCell
            deal={deal}
            canEdit={canEdit}
            editing={editing === "state"}
            saving={saving}
            onEdit={() => setEditing("state")}
            onCancel={() => setEditing(null)}
            onCommit={(next) =>
              void apply(
                {
                  field: "state",
                  body: { state: next },
                  described: `the state to ${dealStateLabel(next)}`,
                },
                deal.version,
              )
            }
          />
        </Td>
        <Td numeric mono>
          <WinProbCell
            deal={deal}
            canEdit={canEdit}
            editing={editing === "win_prob"}
            saving={saving}
            onEdit={() => setEditing("win_prob")}
            onCancel={() => setEditing(null)}
            onCommit={(next) =>
              void apply(
                {
                  field: "win_prob",
                  body: { win_prob: next },
                  described: `the win probability to ${formatPercent(next)}`,
                },
                deal.version,
              )
            }
          />
        </Td>
        <Td mono muted>
          {deal.currency}
        </Td>
        <Td nowrap>
          <span className="mono">
            {deal.start_date_is_firm
              ? formatCalendarDate(deal.start_date_earliest)
              : formatDateRange(deal.start_date_earliest, deal.start_date_latest)}
          </span>
          {/* A range is a range: collapsing it to its first day would read as a
              commitment the data does not make (§07 / D5). */}
          <span className={styles.subLabel}>
            {deal.start_date_is_firm ? "firm" : "range — spreads across months"}
          </span>
        </Td>
        {deal.renewal_date === null ? (
          <Td muted>—</Td>
        ) : (
          <Td mono nowrap>
            {formatCalendarDate(deal.renewal_date)}
          </Td>
        )}
        <Td>
          <LocationCell deal={deal} />
        </Td>
        {canEdit && (
          <Td>
            <div className={styles.rowActions}>
              <Button
                variant="subtle"
                small
                aria-label={`Delete ${deal.customer_name}`}
                onClick={onDelete}
              >
                Delete
              </Button>
            </div>
          </Td>
        )}
      </tr>

      {(stale !== null || failure !== null) && (
        <tr>
          <td colSpan={columnCount} className={styles.messageCell}>
            {stale !== null && (
              <Notice
                tone="warn"
                title="Someone else changed this deal."
                actions={
                  <Button small disabled={saving} onClick={applyToCurrent}>
                    Apply anyway
                  </Button>
                }
              >
                {`This row is from before their edit, so ${stale.described} was not applied. Reload the list to see what they changed, or apply it to the version that exists now.`}
              </Notice>
            )}
            {failure !== null && <ProblemMessage error={failure} action="save that change" />}
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The agreement whose terms this deal actually bills under — the pinned one, or
 * the one inherited from a partner above it. The distinction earns a badge
 * because the two behave differently under a change nobody made to this deal:
 * moving the partner under a new owner re-points an inherited deal's terms and
 * leaves a pinned one exactly where it was.
 *
 * A deal with nothing to resolve gets a loud cell only when the absence costs
 * it something: a clause carrying `share_override_pct` prices itself, so a deal
 * whose every clause states its own split bills fine with no agreement on file.
 * The loud cell is for the deal a report will refuse with `split_unresolvable`.
 * Until the clauses land the cell states the fact and not the verdict.
 */
function AgreementCell({
  deal,
  coverage,
}: {
  deal: DealListItem;
  coverage: SplitCoverage | undefined;
}): ReactNode {
  if (deal.effective_agreement_id === null) {
    return coverage === "needs_agreement" ? (
      <Badge shape="chip" tone="bad">
        No terms resolve
      </Badge>
    ) : (
      <Badge shape="chip" tone="neutral">
        No agreement on file
      </Badge>
    );
  }

  if (!dealInheritsTerms(deal)) return <>{deal.effective_agreement_name}</>;

  return (
    <>
      <span className={styles.inherited}>
        {deal.effective_agreement_name}
        <Badge shape="tag" tone="info">
          Inherited
        </Badge>
      </span>
      <span className={styles.subLabel}>from {deal.effective_agreement_partner_name}</span>
    </>
  );
}

/**
 * Where the customer is, at a glance — and, when nobody has said, that fact
 * rendered as a fact rather than as an empty cell.
 *
 * The two predicates are asking different questions and both are on screen:
 * `dealHasLocation` decides whether the deal counts towards a place at all in
 * the region report, and `dealHasCoordinates` decides whether a map could ever
 * draw it. Collapsing them would make the two enrichment worklists in the
 * toolbar indistinguishable in the rows they filter to.
 */
function LocationCell({ deal }: { deal: DealListItem }): ReactNode {
  if (!dealHasLocation(deal)) {
    return <span className={styles.unlocated}>Not located</span>;
  }

  const detail = [deal.customer_city, deal.customer_region]
    .filter((part): part is string => part !== null && part !== "")
    .join(", ");

  return (
    <>
      <span className={styles.country} title={countryName(deal.customer_country ?? "")}>
        {deal.customer_country}
      </span>
      {!dealHasCoordinates(deal) && (
        <>
          {/* The glyph is decoration; the sentence beside it is what a screen
              reader gets, because "⊘" announced on its own says nothing. */}
          <span
            className={styles.noPin}
            aria-hidden="true"
            title="No coordinates recorded — nothing for a map to draw"
          >
            {" ⊘"}
          </span>
          <span className="srOnly">, no coordinates</span>
        </>
      )}
      {detail !== "" && <span className={styles.subLabel}>{detail}</span>}
    </>
  );
}

interface EditableCellProps {
  deal: DealListItem;
  canEdit: boolean;
  editing: boolean;
  saving: boolean;
  onEdit: () => void;
  onCancel: () => void;
}

function StateCell({
  deal,
  canEdit,
  editing,
  saving,
  onEdit,
  onCancel,
  onCommit,
}: EditableCellProps & { onCommit: (next: DealState) => void }): ReactNode {
  // What the user picked, until the PATCH lands. Falling back to `deal.state`
  // alone would snap the control back to the stored value the instant it was
  // changed, which reads as though the click was ignored.
  const [chosen, setChosen] = useState<DealState | null>(null);

  const badge = (
    <Badge shape="pill" tone={toneFor(dealStateColor(deal.state))}>
      {dealStateLabel(deal.state)}
    </Badge>
  );

  if (!canEdit) return badge;

  if (!editing) {
    return (
      <button
        type="button"
        className={styles.editable}
        aria-label={`Change deal state (currently ${dealStateLabel(deal.state)})`}
        disabled={saving}
        onClick={() => {
          setChosen(null);
          onEdit();
        }}
      >
        {badge}
      </button>
    );
  }

  return (
    <select
      className={styles.inlineControl}
      aria-label="Deal state"
      autoFocus
      disabled={saving}
      value={chosen ?? deal.state}
      onBlur={onCancel}
      onKeyDown={(event) => {
        if (event.key === "Escape") onCancel();
      }}
      onChange={(event) => {
        const next = DealState.safeParse(event.target.value);
        if (!next.success || next.data === deal.state) {
          onCancel();
          return;
        }
        setChosen(next.data);
        onCommit(next.data);
      }}
    >
      {DealState.options.map((option) => (
        <option key={option} value={option}>
          {dealStateLabel(option)}
        </option>
      ))}
    </select>
  );
}

function WinProbCell({
  deal,
  canEdit,
  editing,
  saving,
  onEdit,
  onCancel,
  onCommit,
}: EditableCellProps & { onCommit: (next: number) => void }): ReactNode {
  const [draft, setDraft] = useState("");
  // Set by Escape so the blur that follows knows the edit was abandoned rather
  // than finished — the same keystroke otherwise both cancels and commits.
  const abandoned = useRef(false);

  if (!canEdit) return <>{formatPercent(deal.win_prob)}</>;

  if (!editing) {
    return (
      <button
        type="button"
        className={styles.editable}
        aria-label={`Change win probability (currently ${formatPercent(deal.win_prob)})`}
        disabled={saving}
        onClick={() => {
          setDraft(String(deal.win_prob));
          onEdit();
        }}
      >
        {formatPercent(deal.win_prob)}
      </button>
    );
  }

  const commit = (): void => {
    if (abandoned.current) {
      abandoned.current = false;
      return;
    }
    const next = Number(draft.trim());
    // Anything the contract would reject (`Percent` is 0-100) is treated as
    // "never mind" rather than sent for the API to refuse: an inline edit has
    // nowhere to put a field error.
    if (draft.trim() === "" || !Number.isFinite(next) || next < 0 || next > 100) {
      onCancel();
      return;
    }
    if (next === deal.win_prob) {
      onCancel();
      return;
    }
    onCommit(next);
  };

  return (
    <input
      type="number"
      inputMode="decimal"
      min={0}
      max={100}
      className={`${styles.inlineControl} ${styles.inlineNumber}`}
      aria-label="Win probability"
      autoFocus
      disabled={saving}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          abandoned.current = true;
          onCancel();
        }
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Paging
// ---------------------------------------------------------------------------

function Pager({
  total,
  limit,
  offset,
  onPage,
}: {
  total: number;
  limit: number;
  offset: number;
  onPage: (page: number) => void;
}): ReactNode {
  const page = Math.floor(offset / limit) + 1;
  const lastPage = Math.max(1, Math.ceil(total / limit));
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + limit, total);

  return (
    <div className={styles.tableFoot}>
      <span className={styles.pagerText}>
        {`Showing ${formatCount(first)}–${formatCount(last)} of ${formatCount(total)} deals`}
      </span>
      <div className={styles.pager}>
        <Button small disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <span
          className={styles.pagerText}
        >{`Page ${formatCount(page)} of ${formatCount(lastPage)}`}</span>
        <Button small disabled={page >= lastPage} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
